import React from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import type { Store as StoreType } from '../../services/authService';
import type { StoreFormData } from '../../types/features/storeDetails/storeForm';
import { useEditStoreDialog } from '../../features/storeDetails/storeEdit/hooks/useEditStoreDialog';
import StoreDetailsStoreFormFields from '../../features/storeDetails/storeEdit/components/StoreDetailsStoreFormFields';
import { adminColors } from '../../styles/adminDesignTokens';
import {
    adminButtonSx,
    adminButtonToneSx,
    adminCombineSx,
    adminDialogActionsSx,
    adminDialogContentSx,
    adminDialogSx,
    adminDialogTitleSx,
} from '../../styles/adminComponentStyles';

interface EditStoreDialogProps {
    open: boolean;
    store: StoreType | null;
    onClose: () => void;
    onSave: (storeId: string, data: StoreFormData) => Promise<void>;
    loading: boolean;
    posTypes?: string[];
    countries?: string[];
    statesByCountry?: Record<string, string[]>;
}

const EditStoreDialog: React.FC<EditStoreDialogProps> = ({
    open,
    store,
    onClose,
    onSave,
    loading,
    posTypes,
    countries,
    statesByCountry,
}) => {
    const state = useEditStoreDialog({
        countries,
        onClose,
        onSave,
        open,
        posTypes,
        statesByCountry,
        store,
    });

    return (
        <Dialog
            open={open}
            onClose={state.handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: adminDialogSx }}
        >
            <DialogTitle
                sx={adminCombineSx(adminDialogTitleSx, {
                    backgroundColor: 'rgb(239, 246, 255)',
                    color: adminColors.primary,
                    fontWeight: 600,
                })}
            >
                Edit Store
            </DialogTitle>
            <form onSubmit={state.onSubmit}>
                <DialogContent sx={adminDialogContentSx}>
                    <StoreDetailsStoreFormFields
                        control={state.control}
                        countries={state.countries}
                        errors={state.errors}
                        mappingOptions={state.mappingOptions}
                        posTypes={state.posTypes}
                        selectedCountry={state.selectedCountry}
                        statesByCountry={state.statesByCountry}
                    />
                </DialogContent>
                <DialogActions sx={adminDialogActionsSx}>
                    <Button
                        onClick={state.handleClose}
                        disabled={loading}
                        variant="outlined"
                        size="small"
                        sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        size="small"
                        disabled={loading || !state.isValid}
                        sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary)}
                    >
                        {loading ? <CircularProgress size={20} /> : 'Save Changes'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default EditStoreDialog;
